import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PROGRAMS } from "@/data/programs";
import {
  Utensils, Activity, Brain, GraduationCap, Eye, Wrench, Trophy, ArrowRight,
  Loader2, Check, School, Sparkles, HeartPulse, Rocket,
} from "lucide-react";

const GREEN = "#8BC53F"; // Champions accent

const PATHWAY_SLUGS = ["flight-club", "design-district", "cinemagic-kids", "beats", "krypto-kids"];
const PATHWAYS = PATHWAY_SLUGS.map((s) => PROGRAMS.find((p) => p.slug === s)).filter(Boolean);

const MONTHS = [
  { m: "Month 1", phase: "See + Engage", icon: Eye, text: "Students discover the industry, careers, tools, vocabulary, and possibilities connected to their pathway." },
  { m: "Month 2", phase: "Apply", icon: Wrench, text: "Students work in teams, use real tools and technology, solve problems, build projects, test ideas, and improve their skills." },
  { m: "Month 3", phase: "Achieve", icon: Trophy, text: "Students complete projects, demonstrate what they've learned, present their work, and leave with evidence of achievement." },
];

const EXPERIENCE = [
  { icon: Utensils, title: "Good Food.", text: "Nutritious CACFP meals that help students stay fueled and focused." },
  { icon: HeartPulse, title: "Active Bodies.", text: "Fitness and movement that encourage healthy habits and teamwork." },
  { icon: Brain, title: "Engaged Minds.", text: "Hands-on SEAA challenges that turn learning into execution." },
  { icon: Rocket, title: "Brighter Futures.", text: "Career exposure, entrepreneurship, leadership, and workforce readiness." },
];

const SKILLS = ["Problem Solving", "Communication", "Teamwork", "Leadership", "Creativity", "Critical Thinking", "Financial Awareness", "Career Exploration", "Technology Skills", "Workforce Readiness"];

const inputCls = "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-[#8BC53F]/40 focus:border-[#8BC53F]";
function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function InquiryForm() {
  const [form, setForm] = useState({ school_name: "", contact_name: "", role: "", email: "", phone: "", num_students: "", pathways: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/school-inquiries", form);
      setDone(true);
      toast.success("Thank you! We'll reach out soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="afterschool-success" className="rounded-3xl border border-slate-100 bg-white p-10 text-center shadow-lg">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green-100 text-green-600"><Check className="h-8 w-8" /></span>
        <h3 className="mt-5 font-display text-2xl font-black text-brandnavy">Inquiry received!</h3>
        <p className="mt-2 text-slate-600">Thanks for your interest in bringing Champions × LP Foundation to your school. Our team will be in touch shortly.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-3xl border border-slate-100 bg-white p-6 sm:p-8 shadow-lg space-y-4" data-testid="afterschool-form">
      <div className="flex items-center gap-2">
        <School className="h-5 w-5" style={{ color: GREEN }} />
        <h3 className="font-display text-xl font-bold text-brandnavy">Bring this to your school</h3>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="School / Organization *"><input data-testid="afterschool-school" required value={form.school_name} onChange={update("school_name")} className={inputCls} /></Field>
        <Field label="Your Name *"><input data-testid="afterschool-contact" required value={form.contact_name} onChange={update("contact_name")} className={inputCls} /></Field>
        <Field label="Your Role"><input data-testid="afterschool-role" placeholder="e.g. Principal, AD" value={form.role} onChange={update("role")} className={inputCls} /></Field>
        <Field label="Email *"><input data-testid="afterschool-email" type="email" required value={form.email} onChange={update("email")} className={inputCls} /></Field>
        <Field label="Phone"><input data-testid="afterschool-phone" value={form.phone} onChange={update("phone")} className={inputCls} /></Field>
        <Field label="Approx. # of Students"><input data-testid="afterschool-students" value={form.num_students} onChange={update("num_students")} className={inputCls} /></Field>
      </div>
      <Field label="Pathways of interest">
        <input data-testid="afterschool-pathways" placeholder="e.g. Flight Club, CineMagic Kids" value={form.pathways} onChange={update("pathways")} className={inputCls} />
      </Field>
      <Field label="Tell us about your school & goals *">
        <textarea data-testid="afterschool-message" required rows={4} value={form.message} onChange={update("message")} className={inputCls} />
      </Field>
      <button data-testid="afterschool-submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-4 font-semibold text-brandnavy transition-all hover:-translate-y-0.5 disabled:opacity-60" style={{ background: GREEN }}>
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <School className="h-5 w-5" />} Request Information
      </button>
    </form>
  );
}

export default function Afterschool() {
  return (
    <div data-testid="afterschool-page">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-brandnavy">
        <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full blur-3xl opacity-20" style={{ background: GREEN }} />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-24 sm:py-28">
          <div className="flex items-center gap-3 text-white/90">
            <span className="font-display font-black tracking-tight text-lg" style={{ color: GREEN }}>CHAMPIONS</span>
            <span className="text-white/40 text-xl font-light">×</span>
            <span className="font-display font-extrabold tracking-tight text-lg text-white">LP Foundation</span>
          </div>
          <span className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase text-white">
            <Sparkles className="h-3.5 w-3.5" style={{ color: GREEN }} /> Champions CACFP Afterschool Program
          </span>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.05] max-w-3xl">
            Fuel their potential. <span style={{ color: GREEN }}>Feed their future.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Champions and LP Foundation have joined forces to give schools a complete afterschool experience for the
            <span className="text-white font-semibold"> whole student</span> — nutritious meals, physical activity,
            hands-on SEAA enrichment, and exposure to future careers. Students don't just stay after school. They
            <span className="text-white font-semibold"> eat, move, build, create, explore, and grow.</span>
          </p>
          <div className="mt-9 flex flex-wrap gap-4">
            <a href="#inquiry" data-testid="afterschool-hero-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-semibold text-brandnavy transition-all duration-300 hover:-translate-y-0.5" style={{ background: GREEN }}>
              <School className="h-5 w-5" /> Bring this to your school
            </a>
            <Link to="/programs" className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 font-semibold text-white transition-all duration-300 hover:bg-white/10">
              Explore the pathways <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ONE SOLUTION */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: GREEN }}>One Afterschool Solution</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Two partners. One complete experience.</h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border-t-4 bg-white p-8 shadow-sm" style={{ borderColor: GREEN }}>
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-2xl" style={{ background: `${GREEN}22`, color: GREEN }}><Utensils className="h-6 w-6" /></span>
              <h3 className="font-display text-2xl font-black text-brandnavy">Champions</h3>
            </div>
            <p className="mt-2 text-sm font-semibold uppercase tracking-wide" style={{ color: GREEN }}>Food + Fitness</p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Through the Champions CACFP (Child &amp; Adult Care Food Program), students receive nutritious meals designed
              to keep them fueled, focused, and ready to participate — plus fitness and enrichment that support active
              bodies, healthy habits, teamwork, and engagement.
            </p>
          </div>
          <div className="rounded-3xl border-t-4 border-accent bg-white p-8 shadow-sm">
            <div className="flex items-center gap-3">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-orange-50 text-accent"><Brain className="h-6 w-6" /></span>
              <h3 className="font-display text-2xl font-black text-brandnavy">LP Foundation</h3>
            </div>
            <p className="mt-2 text-sm font-semibold uppercase tracking-wide text-accent">SEAA Enrichment + Workforce</p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              LP Foundation delivers hands-on enrichment through its SEAA Execution System — <strong>Science · Engineering ·
              Athletics · Arts</strong>. Students progress <strong>See → Engage → Apply → Achieve</strong> and learn by doing:
              <strong> Build → Test → Improve → Perform.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* 3-MONTH PATHWAY */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: GREEN }}>3-Month Pathway Experience</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">A structured cycle — not disconnected activities.</h2>
            <p className="mt-4 text-slate-600">Schools select an LP Foundation pathway that students experience over a structured three-month cycle. At the end of each cycle, students can transition into a new pathway.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {MONTHS.map((mo, i) => (
              <div key={mo.m} data-testid={`month-${i + 1}`} className="relative rounded-3xl border border-slate-100 bg-white p-7 shadow-sm">
                <span className="absolute right-6 top-6 font-display text-5xl font-black text-slate-100">{i + 1}</span>
                <span className="grid h-12 w-12 place-items-center rounded-2xl text-brandnavy" style={{ background: `${GREEN}22` }}><mo.icon className="h-6 w-6" style={{ color: GREEN }} /></span>
                <p className="mt-5 text-xs font-semibold uppercase tracking-wide text-slate-400">{mo.m}</p>
                <h3 className="mt-1 font-display text-xl font-bold text-brandnavy">{mo.phase}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{mo.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEAA PATHWAYS */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="max-w-2xl">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: GREEN }}>SEAA Pathways</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Choose the pathways that fit your students.</h2>
          <p className="mt-4 text-slate-600">Pathway availability and rotation can be customized to the needs and interests of each school.</p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {PATHWAYS.map((p) => (
            <div key={p.slug} data-testid={`pathway-card-${p.slug}`} className="group overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl">
              <div className="h-44 overflow-hidden">
                <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" style={{ filter: p.filter }} />
              </div>
              <div className="p-6">
                <h3 className="font-display text-lg font-bold text-brandnavy">{p.name}</h3>
                <p className="mt-1 text-xs font-medium uppercase tracking-wide" style={{ color: p.color }}>{p.tag}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* WHAT STUDENTS DEVELOP */}
      <section className="bg-brandnavy py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: GREEN }}>What Students Develop</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">They don't just hear about the future. They experience it.</h2>
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            {SKILLS.map((s) => (
              <span key={s} className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLETE EXPERIENCE */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: GREEN }}>The Complete Experience</span>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Fueling today. Building tomorrow.</h2>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {EXPERIENCE.map((e) => (
            <div key={e.title} className="rounded-3xl border border-slate-100 bg-white p-6 text-center shadow-sm">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl text-brandnavy" style={{ background: `${GREEN}22` }}><e.icon className="h-7 w-7" style={{ color: GREEN }} /></span>
              <h3 className="mt-5 font-display text-lg font-bold text-brandnavy">{e.title}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{e.text}</p>
            </div>
          ))}
        </div>
        <div className="mx-auto mt-12 max-w-3xl rounded-3xl bg-slate-50 border border-slate-100 p-8 text-center">
          <p className="font-display text-lg font-bold text-brandnavy">Why it works</p>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Nutrition prepares the body. Performance Readiness prepares the student. SEAA creates the experience.
            Workforce exploration connects the experience to the future.
          </p>
        </div>
      </section>

      {/* INQUIRY */}
      <section id="inquiry" className="bg-slate-50 py-20 scroll-mt-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: GREEN }}>Principals & Administrators</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-black tracking-tight text-brandnavy">Let's partner to fuel success.</h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Bring nutrition, fitness, SEAA enrichment, and workforce readiness together in one structured afterschool
              environment. Tell us about your school and we'll build the right experience for your students.
            </p>
            <ul className="mt-6 space-y-3">
              {["Nutritious CACFP meals included", "Hands-on SEAA pathways", "Fitness & enrichment", "Career & workforce exposure"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-slate-700">
                  <span className="grid h-6 w-6 place-items-center rounded-full text-brandnavy" style={{ background: `${GREEN}33` }}><Check className="h-3.5 w-3.5" /></span>{t}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <InquiryForm />
          </div>
        </div>
      </section>
    </div>
  );
}
